import PortalIdParser from 'PortalIdParser';
import memoize from 'transmute/memoize';
import debounce from 'hs-lodash/debounce';
import Raven from 'raven-js';
import { getPropertyValue } from 'marketing-platform-lib/utils/crmUtils';
import { CRM_SEARCH_GRAPHQL_CACHE_KEY } from 'framework-listing-lib/internal/graphql/constants';
const debouncedMissingPropertiesReporter = debounce(() => {
  Raven.captureException(new Error('CRM search result missing allProperties field'));
},
// This exception is meant to help us track down other errors that may
// appear due to the properties being missing
// If we've fetched 100 rows and they're all missing the allProperties field,
// we don't want to report 100 errors. We just want to know this issue
// is occurring generally
60000);
export const transformGraphQLCrmObjectToCrmObject = (graphQLCrmObject, objectTypeId) => {
  var _graphQLCrmObject$all, _graphQLCrmObject$all2, _graphQLCrmObject$ass;
  if (!graphQLCrmObject.allProperties) {
    debouncedMissingPropertiesReporter();
  }
  return {
    objectId: graphQLCrmObject.objectId,
    objectTypeId,
    portalId: Number(PortalIdParser.get()),
    properties: (_graphQLCrmObject$all = (_graphQLCrmObject$all2 = graphQLCrmObject.allProperties) === null || _graphQLCrmObject$all2 === void 0 ? void 0 : _graphQLCrmObject$all2.reduce((acc, graphQLProperty) => {
      var _graphQLProperty$sour;
      acc[graphQLProperty.name] = {
        source: (_graphQLProperty$sour = graphQLProperty.source) !== null && _graphQLProperty$sour !== void 0 ? _graphQLProperty$sour : undefined,
        value: graphQLProperty.value
      };
      return acc;
    }, {})) !== null && _graphQLCrmObject$all !== void 0 ? _graphQLCrmObject$all : {},
    currentUserPermissions: graphQLCrmObject.userPermissions && {
      canView: graphQLCrmObject.userPermissions.currentUserCanView,
      canEdit: graphQLCrmObject.userPermissions.currentUserCanEdit,
      canDelete: graphQLCrmObject.userPermissions.currentUserCanDelete,
      canCommunicate: graphQLCrmObject.userPermissions.currentUserCanCommunicate
    },
    associatedObjects: (_graphQLCrmObject$ass = graphQLCrmObject.associationsBySpec) === null || _graphQLCrmObject$ass === void 0 ? void 0 : _graphQLCrmObject$ass.reduce((acc, association) => {
      var _association$associat, _association$associat2;
      const associationTypeId = (_association$associat = association.associatedObjects) === null || _association$associat === void 0 || (_association$associat = _association$associat.edges[0]) === null || _association$associat === void 0 ? void 0 : _association$associat.associationDefinition.associationTypeId;
      const associationTypeName = (_association$associat2 = association.associatedObjects) === null || _association$associat2 === void 0 || (_association$associat2 = _association$associat2.edges[0]) === null || _association$associat2 === void 0 ? void 0 : _association$associat2.associationDefinition.name;
      if (associationTypeId) {
        var _association$associat3, _association$associat4;
        acc[associationTypeId] = (_association$associat3 = (_association$associat4 = association.associatedObjects) === null || _association$associat4 === void 0 ? void 0 : _association$associat4.edges.map(edge => transformGraphQLCrmObjectToCrmObject(edge.node, objectTypeId))) !== null && _association$associat3 !== void 0 ? _association$associat3 : [];
      }
      if (associationTypeName) {
        var _association$associat5, _association$associat6;
        acc[associationTypeName] = (_association$associat5 = (_association$associat6 = association.associatedObjects) === null || _association$associat6 === void 0 ? void 0 : _association$associat6.edges.map(edge => transformGraphQLCrmObjectToCrmObject(edge.node, objectTypeId))) !== null && _association$associat5 !== void 0 ? _association$associat5 : [];
      }
      return acc;
    }, {})
  };
};
export const transformCrmObjectToGraphQLCrmObject = (crmObject, {
  existingGqlCrmObject
} = {}) => {
  const defaultUserPermissions = (existingGqlCrmObject === null || existingGqlCrmObject === void 0 ? void 0 : existingGqlCrmObject.userPermissions) || {
    currentUserCanView: true,
    currentUserCanEdit: true,
    currentUserCanDelete: true,
    currentUserCanCommunicate: true
  };
  const defaultAssociationsBySpec = (existingGqlCrmObject === null || existingGqlCrmObject === void 0 ? void 0 : existingGqlCrmObject.associationsBySpec) || {};
  return Object.assign({
    id: Number(crmObject.objectId),
    objectId: Number(crmObject.objectId),
    allProperties: Object.keys(crmObject.properties).map(key => {
      var _crmObject$properties;
      return {
        id: `${crmObject.objectId}-${key}`,
        name: key,
        value: getPropertyValue(crmObject, key) || null,
        source: ((_crmObject$properties = crmObject.properties[key]) === null || _crmObject$properties === void 0 ? void 0 : _crmObject$properties.source) || null,
        __typename: 'PropertyValue'
      };
    }),
    userPermissions: crmObject.currentUserPermissions ? {
      currentUserCanView: crmObject.currentUserPermissions.canView,
      currentUserCanEdit: crmObject.currentUserPermissions.canEdit,
      currentUserCanDelete: crmObject.currentUserPermissions.canDelete,
      currentUserCanCommunicate: crmObject.currentUserPermissions.canCommunicate
    } : defaultUserPermissions
  }, crmObject.associatedObjects ? {
    associationsBySpec: Object.keys(crmObject.associatedObjects).map(toObjectTypeDefinitionId => {
      var _crmObject$associated;
      return {
        associatedObjects: {
          edges: ((_crmObject$associated = crmObject.associatedObjects) === null || _crmObject$associated === void 0 ? void 0 : _crmObject$associated[toObjectTypeDefinitionId].map(associatedObject => ({
            node: {
              id: Number(associatedObject.objectId),
              allProperties: Object.keys(associatedObject.properties).map(key => {
                var _associatedObject$pro;
                return {
                  id: `${associatedObject.objectId}-${key}`,
                  name: key,
                  value: associatedObject.properties[key].value ? `${associatedObject.properties[key].value}` : null,
                  source: (_associatedObject$pro = associatedObject.properties[key].source) !== null && _associatedObject$pro !== void 0 ? _associatedObject$pro : null,
                  __typename: 'PropertyValue'
                };
              }),
              objectId: Number(associatedObject.objectId),
              __typename: 'BasicCrmObject'
            },
            associationDefinition: {
              name: toObjectTypeDefinitionId,
              associationTypeId: Number(toObjectTypeDefinitionId),
              associationCategory: 'HUBSPOT_DEFINED'
            }
          }))) || []
        }
      };
    })
  } : defaultAssociationsBySpec, {
    __typename: 'BasicCrmObject'
  });
};

/**
 * This method is transforming the GraphQL response to match the REST response since both are slightly different.
 */
export const transformGraphQLResponseIntoCrmSearchResponse = memoize((graphQLData, objectTypeId) => {
  if (!graphQLData || !(CRM_SEARCH_GRAPHQL_CACHE_KEY in graphQLData)) {
    return undefined;
  }
  const {
    offset,
    results,
    total,
    hasMore
  } = graphQLData[CRM_SEARCH_GRAPHQL_CACHE_KEY];
  return {
    total,
    offset,
    results: results.map(graphqlCrmObject => transformGraphQLCrmObjectToCrmObject(graphqlCrmObject, objectTypeId)),
    hasMore
  };
});
export const transformCrmSearchResponseToGraphQLResponse = crmSearchResponse => {
  return {
    total: crmSearchResponse.total,
    offset: crmSearchResponse.offset,
    results: crmSearchResponse.results.map(crmObject => transformCrmObjectToGraphQLCrmObject(crmObject)),
    hasMore: crmSearchResponse.hasMore
  };
};

/**
 * Add a new CRM object to the Apollo cache.
 * This method is used by optimistic updates in listing-lib.
 */
export const addCrmObjectToApolloCache = (apolloCache, crmObject) => {
  return {
    [CRM_SEARCH_GRAPHQL_CACHE_KEY]: Object.assign({}, apolloCache[CRM_SEARCH_GRAPHQL_CACHE_KEY], {
      results: [transformCrmObjectToGraphQLCrmObject(crmObject), ...apolloCache[CRM_SEARCH_GRAPHQL_CACHE_KEY].results],
      total: apolloCache[CRM_SEARCH_GRAPHQL_CACHE_KEY].total + 1
    })
  };
};

/**
 * Replace existing CRM objects in cache by the ones provided by @crmObjectOrObjects.
 * This method is used by optimistic updates in listing-lib.
 */
export const updateCrmObjectsInApolloCache = (apolloCache, crmObjectOrObjects) => {
  const objectsMap = Array.isArray(crmObjectOrObjects) ? crmObjectOrObjects.reduce((acc, object) => {
    acc[object.objectId] = object;
    return acc;
  }, {}) : {
    [crmObjectOrObjects.objectId]: crmObjectOrObjects
  };
  return {
    [CRM_SEARCH_GRAPHQL_CACHE_KEY]: Object.assign({}, apolloCache[CRM_SEARCH_GRAPHQL_CACHE_KEY], {
      results: apolloCache[CRM_SEARCH_GRAPHQL_CACHE_KEY].results.map(graphqlCrmObject => {
        if (objectsMap[graphqlCrmObject.objectId]) {
          return transformCrmObjectToGraphQLCrmObject(objectsMap[graphqlCrmObject.objectId], {
            existingGqlCrmObject: graphqlCrmObject
          });
        }
        return graphqlCrmObject;
      })
    })
  };
};
function updateExistingGraphqlProperty(graphqlCrmObject, propertyValue) {
  var _graphqlCrmObject$all, _graphqlCrmObject$all2;
  return Object.assign({}, graphqlCrmObject, {
    allProperties: (_graphqlCrmObject$all = (_graphqlCrmObject$all2 = graphqlCrmObject.allProperties) === null || _graphqlCrmObject$all2 === void 0 ? void 0 : _graphqlCrmObject$all2.map(graphQLProperty => {
      if (graphQLProperty.name !== propertyValue.name) {
        return graphQLProperty;
      }
      return Object.assign({}, graphQLProperty, {
        value: propertyValue.value === undefined ? null : propertyValue.value
      });
    })) !== null && _graphqlCrmObject$all !== void 0 ? _graphqlCrmObject$all : []
  });
}
function addNewGraphqlProperty(graphqlCrmObject, propertyValue) {
  var _graphqlCrmObject$all3, _propertyValue$source;
  return Object.assign({}, graphqlCrmObject, {
    allProperties: [...((_graphqlCrmObject$all3 = graphqlCrmObject.allProperties) !== null && _graphqlCrmObject$all3 !== void 0 ? _graphqlCrmObject$all3 : []), {
      id: `${graphqlCrmObject.id}-${propertyValue.name}`,
      name: propertyValue.name,
      value: propertyValue.value || null,
      source: (_propertyValue$source = propertyValue.source) !== null && _propertyValue$source !== void 0 ? _propertyValue$source : null,
      __typename: 'PropertyValue'
    }]
  });
}

/**
 * Update property values from CRM object in cache.
 * This method is used by inline editing in listing-lib.
 */
export const updateGraphQLPropertyValues = (apolloCache, selectedIds, propertyValues) => {
  return {
    [CRM_SEARCH_GRAPHQL_CACHE_KEY]: Object.assign({}, apolloCache[CRM_SEARCH_GRAPHQL_CACHE_KEY], {
      results: apolloCache[CRM_SEARCH_GRAPHQL_CACHE_KEY].results.map(graphqlCrmObject => {
        if (selectedIds.length && !selectedIds.some(id => String(id) === String(graphqlCrmObject.id))) {
          return graphqlCrmObject;
        }
        let copyGraphQLCrmObject = Object.assign({}, graphqlCrmObject);
        for (const propertyValue of propertyValues) {
          var _copyGraphQLCrmObject;
          const isExistingProperty = (_copyGraphQLCrmObject = copyGraphQLCrmObject.allProperties) === null || _copyGraphQLCrmObject === void 0 ? void 0 : _copyGraphQLCrmObject.find(graphqlProperty => graphqlProperty.name === propertyValue.name);
          if (isExistingProperty) {
            copyGraphQLCrmObject = updateExistingGraphqlProperty(copyGraphQLCrmObject, propertyValue);
          } else {
            copyGraphQLCrmObject = addNewGraphqlProperty(copyGraphQLCrmObject, propertyValue);
          }
        }
        return copyGraphQLCrmObject;
      })
    })
  };
};
export const getGraphQLVariables = crmSearchVariables => ({
  associationSpecs: crmSearchVariables.associationSpecs || [],
  hasAssociationSpecs: !!crmSearchVariables.associationSpecs && crmSearchVariables.associationSpecs.length > 0,
  count: crmSearchVariables.fetchParams.count,
  filterGroups: crmSearchVariables.fetchParams.filterGroups,
  objectTypeId: crmSearchVariables.fetchParams.objectTypeId,
  offset: crmSearchVariables.fetchParams.offset,
  query: crmSearchVariables.fetchParams.query,
  sorts: crmSearchVariables.fetchParams.sorts,
  timeZoneId: crmSearchVariables.fetchParams.timeZoneId
});